import {type RoomClientState} from '../../../../../shared/scripts/RoomClientState.ts'
import {UIWGameManager} from '../../../ui_game/UIWGameManager.ts'
import {RoomCreationPhase, type RoomPlayOrderSelectionData, type RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {ActionState, type GameState, PlayerColor, TurnState} from '@colonist/game-states'
import {
    type BuildGameConfigurations,
    ELOTypeEnum,
    ExtensionSettingEnum,
    GameTypeEnum,
    ScenarioSettingEnum,
} from '@colonist/game-base-types'
import {UIGameController} from '../../../../../shared/scripts/ui_game/UIGameController.ts'
import {UIWGameMapController} from '../../../ui_game/ui_game_map/UIWGameMapController.ts'
import {GameClientState} from '../../../../../../shared/GameClientState.ts'
import {UIRoomPlayOrderSelectionView} from './UIRoomPlayOrderSelectionView.ts'
import {uiManager} from '../../../EntryHomepage.ts'
import {Scene} from '../../UISceneTypes.ts'

export class UIRoomPlayOrderSelectionController {
    private temporaryGameManager?: UIWGameManager | undefined
    private playOrderSelectionView?: UIRoomPlayOrderSelectionView | undefined
    private playOrderSelectionData: RoomPlayOrderSelectionData
    private seedOrderSelectionData: RoomSeedOrderSelectionData

    update(roomState: RoomClientState, data: RoomPlayOrderSelectionData) {
        this.playOrderSelectionData = data
        if(roomState.creationPhase != RoomCreationPhase.PlayOrderSelection) return this.revertPlayOrderSelectionInitialization()
        if(this.temporaryGameManager == undefined) this.initializePlayOrderSelection(roomState)
        this.playOrderSelectionView?.update(roomState, data)
    }

    set seedOrder(data: RoomSeedOrderSelectionData) {
        this.seedOrderSelectionData = data
    }

    private initializeMapView(roomState: RoomClientState) {
        if(this.playOrderSelectionData == undefined) return

        // July 2024, Oguz: we need below logic because we cannot initiate map view only with map state yet
        this.temporaryGameManager = new UIWGameManager()
        const buildGameConfigurations = UIRoomPlayOrderSelectionController.getBuildGameConfigurations(roomState, this.playOrderSelectionData)
        this.temporaryGameManager.gameState = new GameClientState()
        this.temporaryGameManager.gameController = new UIGameController(this.temporaryGameManager, buildGameConfigurations)
        // Don't think this is needed. @Oguz? Should be getting initiated in `UIGameController`?
        // this.temporaryGameManager.gameController.mapValidator = new MapValidator(this.playOrderSelectionData.mapState)
        this.temporaryGameManager.canvasController.buildGame()
        this.temporaryGameManager.mapController = new UIWGameMapController(this.temporaryGameManager)
        this.temporaryGameManager.mapController.createMap()
        this.temporaryGameManager.mapController.setInteractionEnabled(true)
        if(this.playOrderSelectionView == undefined) this.playOrderSelectionView = new UIRoomPlayOrderSelectionView(this.temporaryGameManager, roomState, this.playOrderSelectionData, this.seedOrderSelectionData)
    }

    private static getBuildGameConfigurations(roomState: RoomClientState, playOrderSelectionData: RoomPlayOrderSelectionData): BuildGameConfigurations {
        const initialState: GameState = {
            diceState: {
                diceThrown: false,
                dice1: 1,
                dice2: 1,
            },
            bankState: {
                hideBankCards: roomState.gameSetting.hideBankCards,
                resourceCards: {},
            },
            mapState: playOrderSelectionData.mapState,
            tradeState: {
                activeOffers: {},
                closedOffers: {},
                embargoState: {},
            },
            playerStates: {[0]: {
                color: PlayerColor.Black,
                isConnected: true,
                victoryPointsState: {[0]: 1},
                bankTradeRatiosState: {[0]: 1},
                resourceCards: {
                    cards: [],
                },
                cardDiscardLimit: 5,
                isTakingAction: false,
            }},
            currentState: {
                completedTurns: 0,
                turnState: TurnState.InitialPlacement,
                actionState: ActionState.None,
                currentTurnPlayerColor: PlayerColor.None,
                startTime: 0,
                allocatedTime: 0,
            },
            gameLogState: {},
            gameChatState: {},
        }

        return {
            playerColor: PlayerColor.Black,
            playOrder: [PlayerColor.Black],
            gameState: structuredClone(initialState),
            playerUserStates: [],
            gameDetails: {
                matchmakingMatchType: undefined,
                isRanked: false,
                isDiscord: false,
            },
            gameSettings: {
                ...roomState.gameSetting,
                gameType: GameTypeEnum.CreatedTournamentRoom,
                eloType: ELOTypeEnum.Inactive,
                extensionSetting: ExtensionSettingEnum.Players78,
                scenarioSetting: ScenarioSettingEnum.None,
                karmaActive: true,
            },
            timeLeftInState: 0,
            isReplay: true,
        }
    }

    private revertPlayOrderSelectionInitialization() {
        if(this.temporaryGameManager == undefined) return

        const canvasId = this.temporaryGameManager.canvasController.CANVAS_ID
        const canvas = document.getElementById(canvasId)
        canvas?.parentNode?.removeChild(canvas)
        this.playOrderSelectionView?.mainBlock.parentNode?.removeChild(this.playOrderSelectionView.mainBlock)
        this.temporaryGameManager = undefined
        this.playOrderSelectionView = undefined
        uiManager.sceneController.loadScene(Scene.Room)
    }

    private initializePlayOrderSelection(roomState: RoomClientState) {
        this.initializeMapView(roomState)
        uiManager.sceneController.hideAllScenes()
    }
}
